import {
    Close,
    EditOutlined,
    ImageOutlined,
    PaletteOutlined,
    TextFields,
    UploadOutlined,
    ViewQuiltOutlined,
    WarningAmberOutlined,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { HeaderFooterEditor } from './HeaderFooterEditor';

import { quoteFontFamily } from './documentContentStyles';
import {
    DEFAULT_FOOTER_SETTINGS,
    DEFAULT_HEADER_SETTINGS,
    FONT_FAMILIES,
    LINE_HEIGHT_OPTIONS,
    PAGE_SIZES,
    hasGoodContrast,
} from 'config/defaultBranding';
import type {
    BrandingSettings,
    FooterSettings,
    LogoPosition,
    LogoSize,
    PageNumberSettings,
} from 'types';

const fieldLabelSx = { fontSize: 13, fontWeight: 600, mb: 0.75, color: 'text.primary' };

const SectionLabel = ({ children }: { children: ReactNode }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {children}
    </Typography>
);

const ColorSwatch = ({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (next: string) => void;
}) => (
    <Box>
        <Typography sx={fieldLabelSx}>{label}</Typography>
        <TextField
            size="small"
            fullWidth
            value={value}
            onChange={(event) => onChange(event.target.value)}
            slotProps={{
                input: {
                    startAdornment: (
                        <Box
                            component="label"
                            sx={{
                                position: 'relative',
                                width: 22,
                                height: 22,
                                mr: 1,
                                flexShrink: 0,
                                borderRadius: 0.75,
                                bgcolor: value,
                                border: 1,
                                borderColor: 'divider',
                                cursor: 'pointer',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                component="input"
                                type="color"
                                value={value}
                                onChange={(event) => onChange(event.target.value)}
                                sx={{
                                    position: 'absolute',
                                    opacity: 0,
                                    inset: 0,
                                    cursor: 'pointer',
                                }}
                            />
                        </Box>
                    ),
                    sx: { fontFamily: 'monospace', fontSize: 13 },
                },
            }}
        />
    </Box>
);

const SliderRow = ({
    label,
    value,
    min,
    max,
    step,
    unit,
    onChange,
}: {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    unit: string;
    onChange: (next: number) => void;
}) => (
    <Box>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography sx={fieldLabelSx}>{label}</Typography>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>
                {value}
                {unit}
            </Typography>
        </Stack>
        <Slider
            size="small"
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(_, next) => onChange(next as number)}
            sx={{ mt: -0.5 }}
        />
    </Box>
);

interface BrandingPanelProps {
    branding: BrandingSettings;
    onBrandingChange: (branding: BrandingSettings) => void;
}

export function BrandingPanel({ branding, onBrandingChange }: BrandingPanelProps) {
    const [tab, setTab] = useState(0);
    const [isHeaderEditorOpen, setIsHeaderEditorOpen] = useState(false);
    const [isFooterEditorOpen, setIsFooterEditorOpen] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const contrastWarning = !hasGoodContrast(branding.textColor, branding.backgroundColor);

    const readImageFile = (file: File, onLoad: (url: string) => void) => {
        if (file.size > 2 * 1024 * 1024) {
            setUploadError('File size must be less than 2MB.');
            return;
        }
        setUploadError('');
        const reader = new FileReader();
        reader.onload = (event) => {
            const url = event.target?.result;
            if (typeof url === 'string') onLoad(url);
        };
        reader.readAsDataURL(file);
    };

    const pickImage = (onLoad: (url: string) => void) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/png,image/jpeg,image/svg+xml';
        input.onchange = (event) => {
            const file = (event.target as HTMLInputElement).files?.[0];
            if (file) readImageFile(file, onLoad);
        };
        input.click();
    };

    const handleLogoUpload = (type: 'header' | 'footer') => {
        pickImage((url) => {
            onBrandingChange({
                ...branding,
                [type === 'header' ? 'headerLogo' : 'footerLogo']: {
                    url,
                    position: 'center' as LogoPosition,
                    size: 'medium' as LogoSize,
                },
            });
        });
    };

    const handleRemoveLogo = (type: 'header' | 'footer') => {
        onBrandingChange({
            ...branding,
            [type === 'header' ? 'headerLogo' : 'footerLogo']: undefined,
        });
    };

    const handleLogoPositionChange = (type: 'header' | 'footer', position: LogoPosition) => {
        const logoKey = type === 'header' ? 'headerLogo' : 'footerLogo';
        const currentLogo = branding[logoKey];
        if (currentLogo) {
            onBrandingChange({ ...branding, [logoKey]: { ...currentLogo, position } });
        }
    };

    const handleLogoSizeChange = (type: 'header' | 'footer', size: LogoSize) => {
        const logoKey = type === 'header' ? 'headerLogo' : 'footerLogo';
        const currentLogo = branding[logoKey];
        if (currentLogo) {
            onBrandingChange({ ...branding, [logoKey]: { ...currentLogo, size } });
        }
    };

    const handleHeaderImageUpload = () => {
        pickImage((url) => {
            onBrandingChange({
                ...branding,
                header: {
                    ...(branding.header || DEFAULT_HEADER_SETTINGS),
                    image: { url, position: 'center', size: 'medium', maxHeight: 60 },
                },
            });
        });
    };

    const handleFooterImageUpload = () => {
        pickImage((url) => {
            onBrandingChange({
                ...branding,
                footer: {
                    ...(branding.footer || DEFAULT_FOOTER_SETTINGS),
                    image: { url, position: 'center', size: 'medium', maxHeight: 40 },
                },
            });
        });
    };

    const LogoUploader = ({ type }: { type: 'header' | 'footer' }) => {
        const logo = type === 'header' ? branding.headerLogo : branding.footerLogo;
        if (!logo) {
            return (
                <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={<UploadOutlined />}
                    onClick={() => handleLogoUpload(type)}
                >
                    Upload {type === 'header' ? 'header' : 'footer'} logo
                </Button>
            );
        }

        return (
            <Stack spacing={1.25}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                    <Box
                        component="img"
                        src={logo.url}
                        alt={`${type} logo`}
                        sx={{ maxHeight: 40, maxWidth: 120, objectFit: 'contain' }}
                    />
                    <IconButton
                        size="small"
                        aria-label={`Remove ${type} logo`}
                        onClick={() => handleRemoveLogo(type)}
                        sx={{ ml: 'auto', color: 'error.main' }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        fullWidth
                        value={logo.position}
                        onChange={(_, position: LogoPosition | null) => {
                            if (position) handleLogoPositionChange(type, position);
                        }}
                    >
                        <ToggleButton value="left">Left</ToggleButton>
                        <ToggleButton value="center">Center</ToggleButton>
                        <ToggleButton value="right">Right</ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        fullWidth
                        value={logo.size}
                        onChange={(_, size: LogoSize | null) => {
                            if (size) handleLogoSizeChange(type, size);
                        }}
                    >
                        <ToggleButton value="small">S</ToggleButton>
                        <ToggleButton value="medium">M</ToggleButton>
                        <ToggleButton value="large">L</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </Stack>
        );
    };

    const renderRepeatSection = (which: 'header' | 'footer') => {
        const isHdr = which === 'header';
        const section = isHdr ? branding.header : branding.footer;
        const footerSection = !isHdr ? (section as FooterSettings | undefined) : undefined;
        const pageNumbers = footerSection?.pageNumbers;
        const defaults = isHdr ? DEFAULT_HEADER_SETTINGS : DEFAULT_FOOTER_SETTINGS;
        const editorOpen = isHdr ? isHeaderEditorOpen : isFooterEditorOpen;
        const setEditorOpen = isHdr ? setIsHeaderEditorOpen : setIsFooterEditorOpen;
        const enabled = section?.enabled || false;

        return (
            <Box
                sx={{
                    border: 1,
                    borderColor: enabled ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{
                        alignItems: 'center',
                        px: 1.75,
                        py: 1.25,
                        bgcolor: enabled ? 'action.hover' : 'background.default',
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {isHdr ? 'Header' : 'Footer'}
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={enabled}
                                size="small"
                                onChange={(event) =>
                                    onBrandingChange({
                                        ...branding,
                                        [which]: {
                                            ...(section || defaults),
                                            enabled: event.target.checked,
                                        },
                                    })
                                }
                            />
                        }
                        label="Repeat on every page"
                        sx={{
                            mr: 0,
                            '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 600 },
                        }}
                    />
                </Stack>

                <Box sx={{ px: 1.75, py: 1.5 }}>
                    <LogoUploader type={which} />
                </Box>

                {enabled && section ? (
                    <Stack
                        spacing={1.75}
                        sx={{
                            px: 1.75,
                            pb: 1.75,
                            pt: 0.5,
                            borderTop: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Box>
                            <Typography sx={fieldLabelSx}>
                                {isHdr ? 'Header' : 'Footer'} text
                            </Typography>
                            {editorOpen ? (
                                <HeaderFooterEditor
                                    content={section.content}
                                    onChange={(html) =>
                                        onBrandingChange({
                                            ...branding,
                                            [which]: { ...section, content: html },
                                        })
                                    }
                                    onClose={() => setEditorOpen(false)}
                                    type={which}
                                />
                            ) : (
                                <Button
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditOutlined />}
                                    onClick={() => setEditorOpen(true)}
                                >
                                    {section.content ? 'Edit text' : 'Add text'}
                                </Button>
                            )}
                            {section.content && !editorOpen ? (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        mt: 0.75,
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: 1,
                                        bgcolor: 'background.default',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {section.content.replace(/<[^>]+>/g, ' ').trim() ||
                                        'No text yet'}
                                </Typography>
                            ) : null}
                        </Box>

                        <Box>
                            <Typography sx={fieldLabelSx}>Banner image</Typography>
                            {section.image ? (
                                <Stack
                                    direction="row"
                                    spacing={1.25}
                                    sx={{
                                        alignItems: 'center',
                                        px: 1.25,
                                        py: 1,
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={section.image.url}
                                        alt={which}
                                        sx={{ maxHeight: 32, maxWidth: 80, objectFit: 'contain' }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ flex: 1 }}
                                    >
                                        Banner uploaded
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        aria-label={`Remove ${which} banner`}
                                        onClick={() =>
                                            onBrandingChange({
                                                ...branding,
                                                [which]: { ...section, image: undefined },
                                            })
                                        }
                                        sx={{ color: 'error.main' }}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Stack>
                            ) : (
                                <Button
                                    fullWidth
                                    size="small"
                                    variant="text"
                                    startIcon={<UploadOutlined />}
                                    onClick={
                                        isHdr ? handleHeaderImageUpload : handleFooterImageUpload
                                    }
                                >
                                    Upload banner
                                </Button>
                            )}
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={section.showOnFirstPage}
                                    onChange={(event) =>
                                        onBrandingChange({
                                            ...branding,
                                            [which]: {
                                                ...section,
                                                showOnFirstPage: event.target.checked,
                                            },
                                        })
                                    }
                                />
                            }
                            label="Show on the first page"
                            sx={{
                                mx: 0,
                                '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 600 },
                            }}
                        />

                        {!isHdr && footerSection ? (
                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        px: 1.5,
                                        py: 1,
                                        bgcolor: 'background.default',
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Page numbers
                                    </Typography>
                                    <Switch
                                        size="small"
                                        checked={pageNumbers?.enabled || false}
                                        onChange={(event) =>
                                            onBrandingChange({
                                                ...branding,
                                                footer: {
                                                    ...footerSection,
                                                    pageNumbers: {
                                                        ...DEFAULT_FOOTER_SETTINGS.pageNumbers,
                                                        ...(footerSection.pageNumbers || {}),
                                                        enabled: event.target.checked,
                                                    } as PageNumberSettings,
                                                },
                                            })
                                        }
                                    />
                                </Stack>
                                {pageNumbers?.enabled ? (
                                    <Stack spacing={1.25} sx={{ p: 1.5 }}>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1.25}
                                        >
                                            <FormControl size="small" fullWidth>
                                                <InputLabel>Position</InputLabel>
                                                <Select
                                                    value={pageNumbers.position}
                                                    label="Position"
                                                    onChange={(event) =>
                                                        onBrandingChange({
                                                            ...branding,
                                                            footer: {
                                                                ...footerSection,
                                                                pageNumbers: {
                                                                    ...pageNumbers,
                                                                    position: event.target.value as
                                                                        | 'left'
                                                                        | 'center'
                                                                        | 'right',
                                                                },
                                                            },
                                                        })
                                                    }
                                                >
                                                    <MenuItem value="left">Left</MenuItem>
                                                    <MenuItem value="center">Center</MenuItem>
                                                    <MenuItem value="right">Right</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControl size="small" fullWidth>
                                                <InputLabel>Format</InputLabel>
                                                <Select
                                                    value={pageNumbers.format}
                                                    label="Format"
                                                    onChange={(event) =>
                                                        onBrandingChange({
                                                            ...branding,
                                                            footer: {
                                                                ...footerSection,
                                                                pageNumbers: {
                                                                    ...pageNumbers,
                                                                    format: event.target.value as
                                                                        | 'page'
                                                                        | 'page-of-total'
                                                                        | 'page-total',
                                                                },
                                                            },
                                                        })
                                                    }
                                                >
                                                    <MenuItem value="page">Page N</MenuItem>
                                                    <MenuItem value="page-of-total">
                                                        Page N of total
                                                    </MenuItem>
                                                    <MenuItem value="page-total">
                                                        Page N / total
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Prefix"
                                            value={pageNumbers.prefix || ''}
                                            placeholder="Page "
                                            onChange={(event) =>
                                                onBrandingChange({
                                                    ...branding,
                                                    footer: {
                                                        ...footerSection,
                                                        pageNumbers: {
                                                            ...pageNumbers,
                                                            prefix: event.target.value,
                                                        },
                                                    },
                                                })
                                            }
                                        />
                                    </Stack>
                                ) : null}
                            </Box>
                        ) : null}
                    </Stack>
                ) : null}
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
            <Tabs
                value={tab}
                onChange={(_, next) => setTab(next)}
                variant="fullWidth"
                sx={{
                    flexShrink: 0,
                    minHeight: 44,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                        minHeight: 44,
                        py: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        textTransform: 'none',
                    },
                }}
            >
                <Tab
                    icon={<ImageOutlined sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    label="Logos"
                />
                <Tab
                    icon={<PaletteOutlined sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    label="Colors"
                />
                <Tab
                    icon={<TextFields sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    label="Type"
                />
                <Tab
                    icon={<ViewQuiltOutlined sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    label="Layout"
                />
            </Tabs>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, sm: 2.5 }, py: 2 }}>
                {tab === 0 ? (
                    <Stack spacing={1.5}>
                        {uploadError ? (
                            <Alert severity="warning" onClose={() => setUploadError('')}>
                                {uploadError}
                            </Alert>
                        ) : null}
                        <Typography variant="caption" color="text.secondary">
                            PNG, JPG, or SVG. Maximum 2MB.
                        </Typography>
                        {renderRepeatSection('header')}
                        {renderRepeatSection('footer')}
                    </Stack>
                ) : null}

                {tab === 1 ? (
                    <Stack spacing={2}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 2,
                            }}
                        >
                            <ColorSwatch
                                label="Primary"
                                value={branding.primaryColor}
                                onChange={(value) =>
                                    onBrandingChange({ ...branding, primaryColor: value })
                                }
                            />
                            <ColorSwatch
                                label="Secondary"
                                value={branding.secondaryColor}
                                onChange={(value) =>
                                    onBrandingChange({ ...branding, secondaryColor: value })
                                }
                            />
                            <ColorSwatch
                                label="Text"
                                value={branding.textColor}
                                onChange={(value) =>
                                    onBrandingChange({ ...branding, textColor: value })
                                }
                            />
                            <ColorSwatch
                                label="Background"
                                value={branding.backgroundColor}
                                onChange={(value) =>
                                    onBrandingChange({ ...branding, backgroundColor: value })
                                }
                            />
                        </Box>
                        {contrastWarning ? (
                            <Alert
                                severity="warning"
                                icon={<WarningAmberOutlined fontSize="small" />}
                            >
                                Text and background contrast is low. Adjust one of them so the
                                agreement stays readable.
                            </Alert>
                        ) : null}
                    </Stack>
                ) : null}

                {tab === 2 ? (
                    <Stack spacing={2.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Font</InputLabel>
                            <Select
                                value={branding.fontFamily}
                                label="Font"
                                onChange={(event) =>
                                    onBrandingChange({
                                        ...branding,
                                        fontFamily: event.target.value,
                                    })
                                }
                            >
                                {FONT_FAMILIES.map((font) => (
                                    <MenuItem
                                        key={font}
                                        value={font}
                                        sx={{ fontFamily: quoteFontFamily(font) }}
                                    >
                                        {font}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <SliderRow
                            label="Heading size"
                            value={branding.headingFontSize}
                            min={12}
                            max={24}
                            step={1}
                            unit="pt"
                            onChange={(value) =>
                                onBrandingChange({ ...branding, headingFontSize: value })
                            }
                        />
                        <SliderRow
                            label="Body size"
                            value={branding.bodyFontSize}
                            min={8}
                            max={16}
                            step={1}
                            unit="pt"
                            onChange={(value) =>
                                onBrandingChange({ ...branding, bodyFontSize: value })
                            }
                        />
                        <FormControl size="small" fullWidth>
                            <InputLabel>Line height</InputLabel>
                            <Select
                                value={branding.lineHeight}
                                label="Line height"
                                onChange={(event) =>
                                    onBrandingChange({
                                        ...branding,
                                        lineHeight: event.target.value as number,
                                    })
                                }
                            >
                                {LINE_HEIGHT_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <SliderRow
                            label="Space before paragraphs"
                            value={branding.paragraphSpacingBefore}
                            min={0}
                            max={12}
                            step={2}
                            unit="pt"
                            onChange={(value) =>
                                onBrandingChange({ ...branding, paragraphSpacingBefore: value })
                            }
                        />
                        <SliderRow
                            label="Space after paragraphs"
                            value={branding.paragraphSpacingAfter}
                            min={0}
                            max={12}
                            step={2}
                            unit="pt"
                            onChange={(value) =>
                                onBrandingChange({ ...branding, paragraphSpacingAfter: value })
                            }
                        />
                    </Stack>
                ) : null}

                {tab === 3 ? (
                    <Stack spacing={2.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Page size</InputLabel>
                            <Select
                                value={branding.pageSize || 'letter'}
                                label="Page size"
                                onChange={(event) =>
                                    onBrandingChange({
                                        ...branding,
                                        pageSize: event.target.value as 'letter' | 'a4' | 'legal',
                                    })
                                }
                            >
                                {(Object.keys(PAGE_SIZES) as Array<keyof typeof PAGE_SIZES>).map(
                                    (key) => (
                                        <MenuItem key={key} value={key}>
                                            {PAGE_SIZES[key].label}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                        <SectionLabel>Margins</SectionLabel>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 2,
                            }}
                        >
                            <SliderRow
                                label="Top"
                                value={branding.marginTop}
                                min={0.25}
                                max={2}
                                step={0.25}
                                unit="in"
                                onChange={(value) =>
                                    onBrandingChange({ ...branding, marginTop: value })
                                }
                            />
                            <SliderRow
                                label="Bottom"
                                value={branding.marginBottom}
                                min={0.25}
                                max={2}
                                step={0.25}
                                unit="in"
                                onChange={(value) =>
                                    onBrandingChange({ ...branding, marginBottom: value })
                                }
                            />
                            <SliderRow
                                label="Left"
                                value={branding.marginLeft}
                                min={0.25}
                                max={2}
                                step={0.25}
                                unit="in"
                                onChange={(value) =>
                                    onBrandingChange({ ...branding, marginLeft: value })
                                }
                            />
                            <SliderRow
                                label="Right"
                                value={branding.marginRight}
                                min={0.25}
                                max={2}
                                step={0.25}
                                unit="in"
                                onChange={(value) =>
                                    onBrandingChange({ ...branding, marginRight: value })
                                }
                            />
                        </Box>
                    </Stack>
                ) : null}
            </Box>
        </Box>
    );
}
